import { inject, Injectable, OnDestroy } from '@angular/core';
import { Conversation, Message, Paginator } from '@twilio/conversations';
import { Observable, Subject } from 'rxjs';
import { subjectsComplete } from 'src/app/common/app.helpers';
import { NotificationEvent } from '../../../event-notifications/interfaces';
import { findMessageBySid, findMessageIndexBySid } from '../../helpers/twilio-message.helpers';
import { ChatEngineErrorService, ChatEngineOperations } from '../../interfaces/chat-engine/chat-engine-error.service';
import { ChatEngineLifeСycleService } from '../../interfaces/chat-engine/chat-engine-lifecycle-service';
import { ChatEngineNotificationChannelService } from '../../interfaces/chat-engine/chat-engine-notification-channel.service';
import { ChatEngineHandle, ChatTraceData } from '../../interfaces/chat-model.interface';
import { TwilioChatEngineHandle } from './twilio-chat-engine.handle';
import { TwilioErrorService, TwilioInternalOperations } from './twilio-error.service';

type MessageListener = (m: Message) => void;

@Injectable()
export class TwilioNotificationChannelService extends ChatEngineNotificationChannelService implements OnDestroy {
  public get notificationChannelConnectionStateChanged(): Observable<boolean> {
    return this.chatEngineLifeCycleService.connectionStatusChanged;
  }

  public get notificationChannelCleared(): Observable<void> {
    return this.notificationChannelCleared$.asObservable();
  }

  public get notificationChannelInitialMessagesLoaded(): Observable<NotificationEvent[]> {
    return this.notificationChannelInitialMessagesLoaded$.asObservable();
  }

  public get notificationMessageAdded(): Observable<NotificationEvent> {
    return this.notificationMessageAdded$.asObservable();
  }

  public get notificationMessageRemoved(): Observable<string> {
    return this.notificationMessageRemoved$.asObservable();
  }

  private static readonly MAX_NUM_NOTIFICATIONS: number = 1000;

  private notificationChannel: Conversation | null = null;
  private notificationMessages: Message[] = [];

  private messageAddedListener: MessageListener | null = null;
  private messageRemovedListener: MessageListener | null = null;

  private notificationMessageAdded$: Subject<NotificationEvent> = new Subject();
  private notificationMessageRemoved$: Subject<string> = new Subject();
  private notificationChannelCleared$: Subject<void> = new Subject();
  private notificationChannelInitialMessagesLoaded$: Subject<NotificationEvent[]> = new Subject();

  private chatEngineLifeCycleService = inject(ChatEngineLifeСycleService);
  private chatEngineErrorService = inject(ChatEngineErrorService);
  private twilioErrorService = inject(TwilioErrorService);

  public ngOnDestroy(): void {
    subjectsComplete([
      this.notificationMessageAdded$,
      this.notificationMessageRemoved$,
      this.notificationChannelCleared$,
      this.notificationChannelInitialMessagesLoaded$,
    ]);
  }

  public openNotificationChannel(engineHandle: ChatEngineHandle, notificationChannelUniqueName: string): void {
    if (engineHandle.isValid()) {
      this.initializeNotificationChannel(engineHandle as TwilioChatEngineHandle, notificationChannelUniqueName).catch(
        (error: unknown) => {
          this.twilioErrorService.onTwilioInternalOperationFailed(
            TwilioInternalOperations.NOTIFICATIONS_INITIALIZATION,
            error,
            { channelUniqueName: notificationChannelUniqueName } as ChatTraceData,
          );
          this.resetNotificationChannelData();
        },
      );
    }
  }

  public closeNotificationChannel(): void {
    if (this.notificationChannel) {
      if (this.messageAddedListener) {
        this.notificationChannel.removeListener('messageAdded', this.messageAddedListener);
      }
      if (this.messageRemovedListener) {
        this.notificationChannel.removeListener('messageRemoved', this.messageRemovedListener);
      }
    }
    this.resetNotificationChannelData();
    this.notificationChannelCleared$.next();
  }

  public removeNotification(messageId: string): void {
    if (!this.notificationChannel) {
      return;
    }
    const messageIndex: number = findMessageIndexBySid(this.notificationMessages, messageId);
    if (messageIndex !== -1) {
      const message: Message = this.notificationMessages[messageIndex];
      this.notificationMessages.splice(messageIndex, 1);

      const notificationChannelUniqueName: string = this.notificationChannel.uniqueName;
      message.remove().then(
        () => this.notificationMessageRemoved$.next(messageId),
        (error: unknown) => {
          this.chatEngineErrorService.onOperationFailed(ChatEngineOperations.DISMISS_NOTIFICATION, error, {
            channelUniqueName: notificationChannelUniqueName,
            messageId: message.sid,
          } as ChatTraceData);
        },
      );
    }
  }

  private async initializeNotificationChannel(
    engineHandle: TwilioChatEngineHandle,
    notificationChannelUniqueName: string,
  ): Promise<void> {
    const notificationChannel: Conversation =
      await engineHandle.client.getConversationByUniqueName(notificationChannelUniqueName);
    if (!engineHandle.isValid()) {
      return;
    }

    const notificationMessages: Message[] =
      await TwilioNotificationChannelService.fetchNotificationMessages(notificationChannel);
    if (!engineHandle.isValid()) {
      return;
    }

    const notificationEvents: NotificationEvent[] =
      TwilioNotificationChannelService.convertNotificationMessagesToEvents(notificationMessages);
    this.notificationChannelInitialMessagesLoaded$.next(notificationEvents);

    this.notificationChannel = notificationChannel;
    this.notificationMessages = notificationMessages;
    this.subscribeToNotificationMessageEvents();
  }

  private resetNotificationChannelData(): void {
    this.notificationChannel = null;
    this.notificationMessages = [];
  }

  private static async fetchNotificationMessages(notificationChannel: Conversation): Promise<Message[]> {
    const paginator: Paginator<Message> = await notificationChannel.getMessages(
      TwilioNotificationChannelService.MAX_NUM_NOTIFICATIONS,
    );

    return paginator.items;
  }

  private static convertNotificationMessagesToEvents(notificationMessages: Message[]): NotificationEvent[] {
    return notificationMessages.map((message: Message) =>
      TwilioNotificationChannelService.createNotificationEvent(message),
    );
  }

  private subscribeToNotificationMessageEvents(): void {
    this.notificationChannel.on('messageAdded', (message: Message) => {
      if (this.notificationChannel) {
        // Deduplication guard against network noise
        if (findMessageBySid(this.notificationMessages, message.sid) !== null) {
          return;
        }
        this.notificationMessages.push(message);

        const notificationEvent: NotificationEvent = TwilioNotificationChannelService.createNotificationEvent(message);
        this.notificationMessageAdded$.next(notificationEvent);
      }
    });

    this.notificationChannel.on('messageRemoved', (message: Message) => {
      if (this.notificationChannel) {
        const messageIndex: number = findMessageIndexBySid(this.notificationMessages, message.sid);
        if (messageIndex !== -1) {
          this.notificationMessages.splice(messageIndex, 1);
          this.notificationMessageRemoved$.next(message.sid);
        }
      }
    });

    const messageAddedListeners: (() => Message)[] = this.notificationChannel.listeners(
      'messageAdded',
    ) as (() => Message)[];
    const messageRemovedListeners: (() => Message)[] = this.notificationChannel.listeners(
      'messageRemoved',
    ) as (() => Message)[];
    this.messageAddedListener = messageAddedListeners[messageAddedListeners.length - 1] as MessageListener;
    this.messageRemovedListener = messageRemovedListeners[messageAddedListeners.length - 1] as MessageListener;
  }

  private static createNotificationEvent(message: Message): NotificationEvent {
    return {
      ...JSON.parse(message.body),
      message_sid: message.sid,
      message_date_created: message.dateCreated,
    } as NotificationEvent;
  }
}
